/**
 * Durable “receipt PDF download unavailable” for a payment row.
 * Survives remount/refetch so a hard failure (401/403/404/422/500) does not
 * resurrect Download PDF while metadata is unchanged (G2).
 *
 * Memory Map + sessionStorage (moreRouteUtils / paymentSurfaceAuthSeen pattern).
 * Key: `customer:receipt-download-blocked:{tenantCode}:{transactionId}`
 * Heal: clear when downloadPath differs from the failing path and the row is
 * downloadable again (`canDownloadInternalReceipt`).
 */

import { canDownloadInternalReceipt, type ReceiptRowLike } from './accountReceiptDownload.js';

const KEY_PREFIX = 'customer:receipt-download-blocked:';

export interface ReceiptDownloadBlockRecord {
  /** downloadPath at the moment of hard failure (null/empty if none). */
  readonly failingDownloadPath: string;
}

const memoryBlocks = new Map<string, ReceiptDownloadBlockRecord>();

export function receiptDownloadBlockedStorageKey(
  tenantCode: string,
  transactionId: number,
): string {
  return `${KEY_PREFIX}${tenantCode}:${transactionId}`;
}

function normalizePath(path: string | null | undefined): string {
  return path?.trim() ?? '';
}

function readStorage(): Storage | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
    return null;
  }
  return window.sessionStorage;
}

function parseRecord(raw: string | null): ReceiptDownloadBlockRecord | null {
  if (raw === null || raw.length === 0) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'failingDownloadPath' in parsed &&
      typeof (parsed as { failingDownloadPath: unknown }).failingDownloadPath === 'string'
    ) {
      return {
        failingDownloadPath: (parsed as ReceiptDownloadBlockRecord).failingDownloadPath,
      };
    }
  } catch {
    // Legacy / corrupt — treat as blocked with empty path fingerprint.
  }
  // Plain "1" or unparseable: still blocked, no path fingerprint → heal needs new path + ready.
  return { failingDownloadPath: '' };
}

function readDurableRecord(
  tenantCode: string,
  transactionId: number,
): ReceiptDownloadBlockRecord | null {
  const key = receiptDownloadBlockedStorageKey(tenantCode, transactionId);
  const fromMemory = memoryBlocks.get(key);
  if (fromMemory !== undefined) {
    return fromMemory;
  }
  const storage = readStorage();
  if (storage === null) {
    return null;
  }
  try {
    const record = parseRecord(storage.getItem(key));
    if (record !== null) {
      memoryBlocks.set(key, record);
    }
    return record;
  } catch {
    return null;
  }
}

function writeDurableRecord(
  tenantCode: string,
  transactionId: number,
  record: ReceiptDownloadBlockRecord,
): void {
  const key = receiptDownloadBlockedStorageKey(tenantCode, transactionId);
  memoryBlocks.set(key, record);
  const storage = readStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(key, JSON.stringify(record));
  } catch {
    // sessionStorage may be unavailable (private mode) — memory still works.
  }
}

/** Mark this row unavailable after a hard PDF download failure. */
export function markReceiptDownloadBlocked(
  tenantCode: string,
  transactionId: number,
  failingDownloadPath: string | null | undefined,
): void {
  if (tenantCode.length === 0 || !Number.isFinite(transactionId)) {
    return;
  }
  writeDurableRecord(tenantCode, transactionId, {
    failingDownloadPath: normalizePath(failingDownloadPath),
  });
}

export function clearReceiptDownloadBlocked(
  tenantCode: string,
  transactionId: number,
): void {
  const key = receiptDownloadBlockedStorageKey(tenantCode, transactionId);
  memoryBlocks.delete(key);
  const storage = readStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * True when a hard failure was recorded and list metadata has not healed
 * (same failing path, or still not downloadable).
 * Clears the durable block when path changed and row is downloadable again.
 */
export function isReceiptDownloadBlocked(
  tenantCode: string,
  transactionId: number,
  row: ReceiptRowLike,
): boolean {
  if (tenantCode.length === 0 || !Number.isFinite(transactionId)) {
    return false;
  }
  if (row.transactionId !== transactionId) {
    clearReceiptDownloadBlocked(tenantCode, transactionId);
    return false;
  }
  const record = readDurableRecord(tenantCode, transactionId);
  if (record === null) {
    return false;
  }
  const currentPath = normalizePath(row.downloadPath);
  const healedPath = currentPath !== record.failingDownloadPath;
  if (healedPath && canDownloadInternalReceipt(row)) {
    clearReceiptDownloadBlocked(tenantCode, transactionId);
    return false;
  }
  return true;
}

/** Test helper — wipe memory + sessionStorage blocks. */
export function resetReceiptDownloadBlockedForTests(): void {
  memoryBlocks.clear();
  const storage = readStorage();
  if (storage === null) {
    return;
  }
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null && key.startsWith(KEY_PREFIX)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      storage.removeItem(key);
    }
  } catch {
    // ignore
  }
}
